import { useRef, useState } from 'react';
import I18n from 'I18n';
import UIDropZone from 'UIComponents/drop/UIDropZone';
import UIFileInput from 'UIComponents/input/UIFileInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UILink from 'UIComponents/link/UILink';
import UIFlex from 'UIComponents/layout/UIFlex';
import { theme } from 'academy-common-lib/utils/Styling';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const FileInput = ({
  fileInputRef,
  disabled,
  onChange,
  file,
  minWidth,
  minHeight,
  domNodeRef
}) => {
  return /*#__PURE__*/_jsx(UIFileInput, {
    accept: VALID_IMAGE_TYPES,
    "aria-label": I18n.text('learningCenter.cropper.uploadAriaLabel'),
    disabled: disabled,
    domNodeRef: domNodeRef,
    onChange: onChange,
    ref: fileInputRef,
    selectLabel: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      direction: "column",
      gap: 12,
      children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "learningCenter.cropper.modalDropZone",
        options: {
          browseLink: /*#__PURE__*/_jsx(UILink, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.cropper.modalDropZoneBrowseLink"
            })
          })
        }
        // @ts-expect-error Missing attribute
        ,
        style: {
          maxWidth: 224,
          textAlign: 'center'
        }
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.cropper.sizeHelpText",
        options: {
          minWidth,
          minHeight
        }
      })]
    }),
    use: file ? 'tertiary-light' : 'unstyled',
    value: file
  });
};
export const ModalDropZone = ({
  file,
  handleCropperUpload,
  handleFileUpdate,
  minImageHeight,
  minImageWidth
}) => {
  const [dropzoneUse, setDropzoneUse] = useState('default');
  const [error, setError] = useState();
  const [fileUploading, setFileUploading] = useState(false);
  const fileInputRef = useRef(null);
  const fileInputDomNodeRef = useRef(null);
  const onError = errorText => {
    setDropzoneUse('error');
    handleFileUpdate(null);
    setError(errorText);
    handleCropperUpload(null);
  };
  const readFile = newFile => {
    const reader = new FileReader();
    setFileUploading(true);
    reader.onload = () => {
      handleFileUpdate(newFile);
      setFileUploading(false);
      setDropzoneUse('success');
      setError(undefined);
      handleCropperUpload(reader.result);
    };
    reader.readAsDataURL(newFile);
  };
  const verifyImage = newFile => {
    const fileType = newFile.type;
    if (!VALID_IMAGE_TYPES.includes(fileType)) {
      if (fileType.startsWith('image/')) onError(I18n.text('learningCenter.cropper.onlyValidImagesError'));else onError(I18n.text('learningCenter.cropper.onlyImageError'));
      return;
    }
    const image = new Image();
    image.onload = () => {
      const height = image.height;
      const width = image.width;
      if (height < minImageHeight || width < minImageWidth) {
        onError(I18n.text('learningCenter.cropper.invalidDimensions', {
          minWidth: minImageWidth,
          minHeight: minImageHeight
        }));
        return;
      }
      readFile(newFile);
    };
    image.onerror = () => onError(I18n.text('learningCenter.cropper.readFileError'));
    image.src = URL.createObjectURL(newFile);
  };
  const handleDragOver = e => {
    e.preventDefault();
    setDropzoneUse('hover');
  };
  const handleDragExit = e => {
    e.preventDefault();
    setDropzoneUse('default');
  };
  const handleFileChange = e => {
    e.preventDefault();
    if (!e.target.value) return;
    verifyImage(e.target.value);
  };
  const handleDrop = e => {
    e.preventDefault();
    if (e.dataTransfer.files.length !== 1) {
      onError(I18n.text('learningCenter.cropper.oneFileOnlyError'));
      return;
    }
    verifyImage(e.dataTransfer.files[0]);
  };
  const handleDropClick = e => {
    const inputNode = fileInputDomNodeRef.current;
    if (inputNode && !inputNode.contains(e.target)) {
      fileInputRef.current.handleClick();
    }
  };
  const renderFileInput = () => /*#__PURE__*/_jsx(FileInput, {
    disabled: fileUploading,
    domNodeRef: fileInputDomNodeRef,
    file: file,
    fileInputRef: fileInputRef,
    minHeight: minImageHeight,
    minWidth: minImageWidth,
    onChange: handleFileChange
  });
  return error || !file ? /*#__PURE__*/_jsxs(UIDropZone, {
    "data-test-id": "thumbnail-upload-drop-zone",
    iconName: "upload",
    onClick: handleDropClick,
    onDragEnd: handleDragExit,
    onDragEnter: handleDragOver,
    onDragLeave: handleDragExit,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    size: "large",
    style: {
      height: 300,
      display: 'flex',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: theme.colors.BATTLESHIP
    },
    use: dropzoneUse,
    children: [!!error && /*#__PURE__*/_jsx("p", {
      children: error
    }), renderFileInput()]
  }) : renderFileInput();
};
export default ModalDropZone;